import { C, S } from "../customers/theme";
import { SO_STATUSES } from "../lib/salesOrders";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const n3 = (x) => Math.round((Number(x) || 0) * 1000) / 1000;
const statusOf = (v) => SO_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };

export function SOStatusBadge({ status }) {
  const s = statusOf(status);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

export default function SalesOrderView({ order, onClose, onEdit, onCreateInvoice, busy }) {
  const lines = (order.sales_order_lines || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const cust = order.customers || {};
  const cur = order.preferred_currency === "GBP" ? "£" : order.preferred_currency === "USD" ? "$" : "€";
  const totalReserved = lines.reduce((s, l) => s + n3(l.reserved_quantity), 0);
  const totalOrdered = lines.reduce((s, l) => s + n3(l.quantity), 0);
  const short = totalReserved < totalOrdered;
  const canInvoice = ["confirmed", "partially_fulfilled", "fulfilled"].includes(order.status);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{order.so_number}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3 }}>{order.customer_name || cust.company_name || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <SOStatusBadge status={order.status} />
            <button onClick={() => onEdit(order)} style={S.btn("secondary")}>Edit</button>
            {onCreateInvoice && canInvoice && (
              <button onClick={() => onCreateInvoice(order)} style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} disabled={busy} title="Create an invoice from this sales order">
                {busy ? "Creating…" : "Create Invoice →"}
              </button>
            )}
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 14 }}>
          <div>
            <div style={S.sectionTitle}>Customer</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
              <div><strong>{order.customer_name || cust.company_name || "—"}</strong></div>
              {cust.email && <div style={{ color: C.textMuted }}>✉️ {cust.email}</div>}
              {cust.phone && <div style={{ color: C.textMuted }}>☎️ {cust.phone}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Order</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: C.textMuted }}>
              <div>Order date: <span style={{ color: C.text }}>{order.order_date || "—"}</span></div>
              <div>Requested delivery: <span style={{ color: C.text }}>{order.requested_delivery_date || "—"}</span></div>
              <div>Salesperson: <span style={{ color: C.text }}>{order.salesperson || "—"}</span></div>
              {order.reference && <div>Reference: <span style={{ color: C.text }}>{order.reference}</span></div>}
            </div>
          </div>
        </div>

        <div style={S.sectionTitle}>Line Items</div>
        <table style={S.table}>
          <thead><tr>
            {["Product", "SKU", "Qty", "Reserved", "Unit Price", "Disc%", "VAT%", "Line Total"].map((h) => <th key={h} style={S.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={8} style={S.empty}>No lines</td></tr> : lines.map((l) => {
              const lineShort = n3(l.reserved_quantity) < n3(l.quantity);
              return (
                <tr key={l.id}>
                  <td style={S.td}>{l.description || "—"}</td>
                  <td style={S.td}>{l.sku || "—"}</td>
                  <td style={S.td}>{n3(l.quantity)}</td>
                  <td style={S.td}><span style={{ color: lineShort ? C.warning : C.success, fontWeight: 600 }}>{n3(l.reserved_quantity)}</span></td>
                  <td style={S.td}>{money(l.unit_price, cur)}</td>
                  <td style={S.td}>{n3(l.discount_percent)}</td>
                  <td style={S.td}>{n3(l.vat_rate)}</td>
                  <td style={S.td}>{money(l.line_total, cur)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 12, gap: 16, flexWrap: "wrap" }}>
          <div style={{ background: C.surface2, borderRadius: 8, padding: "10px 16px", fontSize: 13 }}>
            <span style={{ color: C.textMuted }}>Reserved </span><strong style={{ color: short ? C.warning : C.success }}>{totalReserved}</strong>
            <span style={{ color: C.textMuted }}> of {totalOrdered} ordered</span>
            {short && <div style={{ color: C.warning, fontSize: 12, marginTop: 4 }}>⚠ Some quantity is unreserved (insufficient stock).</div>}
          </div>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(order.subtotal, cur)}</strong></div>
            {order.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(order.total_discount, cur)}</strong></div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>VAT</span><strong>{money(order.total_vat, cur)}</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(order.total_amount, cur)}</span></div>
          </div>
        </div>

        {order.notes && (<><div style={S.sectionTitle}>Notes</div><div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{order.notes}</div></>)}
      </div>
    </div>
  );
}
